/**
 * Merges a base form schema with backend system-field metadata into a
 * CFormBuilder-compatible schema.
 *
 * For each key in `schema`, the matching entry in `systemFields` (looked up by
 * the same key) overrides `disabled` and `hint`. Options from the base field and
 * the system field are concatenated (base first), letting the backend extend the
 * option list without redefining it.
 *
 * Keys absent from `systemFields` are omitted — only fields the backend exposes
 * (i.e. the user is permitted to see) reach the resulting schema.
 *
 * `persistentFields` are keys that must always stay in the form even though the
 * backend does not deliver them as system fields (they do not follow system-field
 * permission rules), regardless of whether the section exposes any system field.
 * Since they have no backend metadata to inherit `disabled` from, they inherit the
 * section's aggregate read-only state: disabled when every delivered system field
 * is disabled, editable otherwise (or editable when the section has none).
 *
 * @param {Object<string, Object>} [schema={}] - Base form schema keyed by field key.
 * @param {Object<string, {
 *   disabled?: boolean,
 *   hint?: string,
 *   mandatory?: boolean,
 *   compulsory?: boolean,
 *   options?: Array
 * }>} [systemFields={}] - Backend system-field metadata keyed by field key.
 * @param {string[]} [persistentFields=[]] - Keys kept in the form regardless of
 * system-field permission (gated only by the section being non-empty).
 * @returns {Object<string, Object>} Merged schema ready for CFormBuilder.
 */
export const createSystemFieldsSchema = (schema = {}, systemFields = {}, persistentFields = []) => {
  const hasSystemFields = Object.keys(systemFields).length > 0
  const sectionReadOnly = hasSystemFields
    && Object.values(systemFields).every((field) => field.disabled)

  return Object.entries(schema).reduce((acc, [ key, baseField ]) => {
    const systemField = systemFields[key]
    const isPersistent = persistentFields.includes(key)

    if (!systemField && !isPersistent) return acc

    const options = [
      ...baseField?.options || [],
      ...systemField?.options || []
    ]

    const disabled = baseField?.disabled ?? (systemField
      ? systemField.disabled
      : sectionReadOnly)

    return {
      ...acc,
      [key]: {
        ...baseField || {},
        ...(options.length ? { options } : {}),
        ...(systemField?.hint ? { hint: systemField.hint } : {}),
        disabled,
        ...(baseField?.isDisabled ? {
          isDisabled: (formValue) => disabled || baseField?.isDisabled(formValue),
        } : {}),
      }
    }
  }, {})
}
